import {Stack, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useShallowSelector} from "../../shared/hooks";
import {userSelector} from "../../entities/user";
import {useEffect} from "react";
import {routes} from "../../app/providers/router/routes";
import {navigate} from "@storybook/addon-links";
import {ConnectButton} from "../../features/connect-button";
import {useNavigate} from "react-router-dom";

import { useState } from "react";

const t = {
    en: {
        label: "Research Lab Manifesto",
        subtitle: "An interdisciplinary research environment dedicated to making love visible",
        thesis1: "Love has always been felt. Spoken of. Sung about. Promised.",
        thesis2: "But never truly",
        thesisSeen: "seen",
        thesis3: "We are building the instruments that make the invisible — visible.",
        problemLabel: "The Problem",
        problem1: "Humanity has thousands of years of poetry about love, centuries of philosophy, decades of psychology — and yet two people sitting across from each other still cannot answer a simple question: what exactly do I give, and what exactly do I need?",
        problem2: "We lack instruments. Not instruments of measurement, but instruments of seeing. A telescope does not create stars — it reveals what was always there. That is what we build: telescopes for love.",
        approachLabel: "The Approach",
        approach1: "We combine what has never been combined: Vedic wisdom and blockchain protocols. Field anthropology and digital ontology. Chakral diagnostics and quantified exchange. Philosophical inquiry and working code.",
        approach2: "This is not therapy. This is not a dating app. This is a research environment where every project answers the same question from a different angle: can love be made visible — and what happens when it is?",
        instrumentsLabel: "The Instruments",
        film: { role: "Documentary Film", desc: "A documentary across 13 countries — capturing how different cultures see, practice, and transmit love. From Mosuo matriarchies to Maya ceremonies. Raw empirics." },
        book: { role: "The Book", desc: "The author’s journey through all colors of love — mapped through the chakra system, grounded in philosophy, told through encounters with real people and real places." },
        lecture: { name: "The Lecture", role: "Theoretical Framework", desc: "An anthology of love from the Vedas to neuroscience. The intellectual backbone — historical, cross-cultural, philosophical foundations of the research program." },
        chakroscope: { role: "Diagnostic Instrument", desc: "A patented self-reflection tool mapping inner states through the chakral system. The mirror — before you can see love in another, you must see yourself." },
        exchange: { name: "The Exchange Map", role: "Communication Bridge", desc: "A new instrument for quantifying what you give and what you seek in a relationship. Making expectations visible, explicit, and honest — before they become conflicts." },
        protocol: { role: "Ontological Protocol", desc: "The world’s first functioning blockchain wedding platform. A promise — spoken privately, recorded permanently, made visible forever. The ritual for the digital age." },
        researcherLabel: "The Researcher",
        authorName: "Yury Reut",
        authorDesc: "Philosopher. Entrepreneur. PhD candidate in digital ontology. Five years of dedicated research at the intersection of technology and meaning. A journey from the Vedas to blockchain, from Bali to Belarus, from inner silence to public protocols — all asking the same question.",
        tags: ["Philosophy", "Blockchain", "Anthropology", "Film", "Vedanta", "Ontology"],
        closing1: "Love was never invisible.",
        closing2: "We just didn’t have",
        closing3: "the instruments.",
    },
    ru: {
        label: "Манифест исследовательской лаборатории",
        subtitle: "Междисциплинарная исследовательская среда, посвящённая тому, чтобы сделать любовь видимой",
        thesis1: "Любовь всегда чувствовали. О ней говорили. Пели. Обещали.",
        thesis2: "Но никогда по-настоящему не",
        thesisSeen: "видели",
        thesis3: "Мы создаём инструменты, которые делают невидимое — видимым.",
        problemLabel: "Проблема",
        problem1: "У человечества тысячи лет поэзии о любви, века философии, десятилетия психологии — и всё же два человека, сидящих напротив друг друга, по-прежнему не могут ответить на простой вопрос: что именно я даю и что именно мне нужно?",
        problem2: "Нам не хватает инструментов. Не инструментов измерения, а инструментов видения. Телескоп не создаёт звёзды — он открывает то, что всегда было. Именно это мы строим: телескопы для любви.",
        approachLabel: "Подход",
        approach1: "Мы соединяем то, что никогда не соединяли: ведическую мудрость и блокчейн-протоколы. Полевую антропологию и цифровую онтологию. Чакральную диагностику и оцифрованный обмен. Философский поиск и работающий код.",
        approach2: "Это не терапия. Это не приложение для знакомств. Это исследовательская среда, где каждый проект отвечает на один и тот же вопрос с разных сторон: можно ли сделать любовь видимой — и что происходит, когда это удаётся?",
        instrumentsLabel: "Инструменты",
        film: { role: "Документальный фильм", desc: "Документальный фильм через 13 стран — о том, как разные культуры видят, практикуют и передают любовь. От матриархата мосо до церемоний майя. Чистая эмпирика." },
        book: { role: "Книга", desc: "Путешествие автора через все цвета любви — картографированное через систему чакр, укоренённое в философии, рассказанное через встречи с реальными людьми и реальными местами." },
        lecture: { name: "Лекция", role: "Теоретическая рамка", desc: "Антология любви от Вед до нейронауки. Интеллектуальный каркас — исторические, кросс-культурные, философские основания исследовательской программы." },
        chakroscope: { role: "Диагностический инструмент", desc: "Запатентованный инструмент самопознания, картографирующий внутренние состояния через чакральную систему. Зеркало — прежде чем увидеть любовь в другом, нужно увидеть себя." },
        exchange: { name: "Карта обмена", role: "Коммуникационный мост", desc: "Новый инструмент для оцифровки того, что ты даёшь и чего ищешь в отношениях. Делает ожидания видимыми, явными и честными — до того, как они превратятся в конфликты." },
        protocol: { role: "Онтологический протокол", desc: "Первая в мире действующая блокчейн-платформа для бракосочетаний. Обещание — произнесённое приватно, записанное навсегда, сделанное видимым навечно. Ритуал цифровой эпохи." },
        researcherLabel: "Исследователь",
        authorName: "Юрий Реут",
        authorDesc: "Философ. Предприниматель. Аспирант в области цифровой онтологии. Пять лет исследований на стыке технологий и смысла. Путь от Вед к блокчейну, от Бали до Беларуси, от внутренней тишины к публичным протоколам — и один вопрос сквозь всё.",
        tags: ["Философия", "Блокчейн", "Антропология", "Кино", "Веданта", "Онтология"],
        closing1: "Любовь никогда не была невидимой.",
        closing2: "Просто у нас не было",
        closing3: "инструментов.",
    },
};


const Divider = () => <div style={{ width: "100%", height: "1px", background: "rgba(245,237,227,0.1)", margin: "0 auto", maxWidth: "900px" }} />;

// @ts-ignore
const Section = ({ label, children }) => (
    <section style={{ maxWidth: 900, margin: "0 auto", padding: "100px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "40px" }}>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: 3, textTransform: "uppercase", color: "#8A7D74" }}>{label}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>{children}</div>
    </section>
);

// @ts-ignore
const Prose = ({ text }) => (
    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", fontWeight: 300, lineHeight: 1.6, color: "#F5EDE3", margin: 0 }}>{text}</p>
);

// @ts-ignore
const Card = ({ num, icon, name, role, desc }) => (
    <div style={{ padding: "32px", border: "1px solid rgba(245,237,227,0.08)", position: "relative" }}>
        <div style={{ position: "absolute", top: 16, right: 16, fontSize: 10, fontFamily: "'DM Sans', sans-serif", color: "#8A7D74", opacity: 0.5 }}>{num}</div>
        <div style={{ fontSize: "24px", marginBottom: 20 }}>{icon}</div>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: "#C4534A", marginBottom: 8 }}>{role}</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", marginBottom: 16, color: "#F5EDE3" }}>{name}</div>
        <p style={{ fontSize: "0.9rem", color: "#8A7D74", lineHeight: 1.6, margin: 0 }}>{desc}</p>
    </div>
);

// --- Основной компонент ---

export const About = () => {
    const [lang, setLang] = useState("en");
    // @ts-ignore
    const s = t[lang];

    // @ts-ignore
    // @ts-ignore
    return (
        <div style={{ background: "#1A1412", color: "#F5EDE3", fontFamily: "'DM Sans', sans-serif", fontWeight: 300, lineHeight: 1.7, minHeight: "100vh" }}>
            <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />

            {/* Language Toggle */}
            <div style={{ position: "fixed", top: 32, right: 32, zIndex: 1000, display: "flex", background: "rgba(26,20,18,0.8)", backdropFilter: "blur(10px)", border: "1px solid rgba(245,237,227,0.1)" }}>
                {["en", "ru"].map((l) => (
                    <button key={l} onClick={() => setLang(l)} style={{ padding: "8px 16px", background: lang === l ? "#C4534A" : "transparent", color: "#F5EDE3", border: "none", cursor: "pointer", fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", transition: "0.3s" }}>{l}</button>
                ))}
            </div>

            {/* Hero */}
            <section style={{ height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: "0 24px", position: "relative" }}>
                <div style={{ position: "absolute", width: "100%", height: "100%", background: "radial-gradient(circle at center, rgba(196,83,74,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />
                <div style={{ fontSize: 11, letterSpacing: 5, textTransform: "uppercase", color: "#8A7D74", marginBottom: 40 }}>{s.label}</div>
                <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.5rem, 7vw, 6rem)", fontWeight: 300, lineHeight: 1.1, margin: 0 }}>
                    Now You Can<br /><em style={{ fontStyle: "italic", color: "#C4534A" }}>See Love</em>
                </h1>
                <p style={{ maxWidth: 600, marginTop: 40, color: "#8A7D74", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem" }}>{s.subtitle}</p>
            </section>

            {/* Thesis */}
            <section style={{ padding: "120px 24px", textAlign: "center", maxWidth: 850, margin: "0 auto" }}>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", lineHeight: 1.5 }}>
                    {s.thesis1}<br />
                    {s.thesis2} <span style={{ color: "#E8C87E", borderBottom: "1px solid #E8C87E" }}>{s.thesisSeen}</span>.
                    <br /><br />
                    <span style={{ fontSize: "1.2rem", color: "#8A7D74" }}>{s.thesis3}</span>
                </p>
            </section>

            <Divider />

            <Section label={s.problemLabel}>
                <Prose text={s.problem1} />
                <Prose text={s.problem2} />
            </Section>

            <Divider />

            <Section label={s.approachLabel}>
                <Prose text={s.approach1} />
                <Prose text={s.approach2} />
            </Section>

            <Divider />

            {/* Instruments Grid */}
            <section style={{ maxWidth: 1100, margin: "0 auto", padding: "120px 24px" }}>
                <div style={{ textAlign: "center", marginBottom: 80 }}>
                    <div style={{ fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: "#8A7D74" }}>{s.instrumentsLabel}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "24px" }}>
                    <Card num="01" icon="🎬" role={s.film.role} name={s.film.name} desc={s.film.desc} />
                    <Card num="02" icon="📖" role={s.book.role} name={s.book.name} desc={s.book.desc} />
                    <Card num="03" icon="🎙️" role={s.lecture.role} name={s.lecture.name} desc={s.lecture.desc} />
                    <Card num="04" icon="👁️" role={s.chakroscope.role} name={s.chakroscope.name} desc={s.chakroscope.desc} />
                    <Card num="05" icon="🗺️" role={s.exchange.role} name={s.exchange.name} desc={s.exchange.desc} />
                    <Card num="06" icon="🔗" role={s.protocol.role} name={s.protocol.name} desc={s.protocol.desc} />
                </div>
            </section>

            {/* Researcher */}
            <section style={{ background: "rgba(245,237,227,0.02)", padding: "120px 24px" }}>
                <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
                    <div style={{ fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: "#C4534A", marginBottom: 32 }}>{s.researcherLabel}</div>
                    <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.5rem", fontWeight: 300, marginBottom: 24 }}>{s.authorName}</h2>
                    <p style={{ color: "#8A7D74", fontSize: "1.1rem", lineHeight: 1.8, marginBottom: 40 }}>{s.authorDesc}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12 }}>
                        {s.tags.map((tag: string) => (
                            <span key={tag} style={{ border: "1px solid rgba(245,237,227,0.1)", padding: "6px 16px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>{tag}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Closing */}
            <section style={{ padding: "150px 24px", textAlign: "center" }}>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.5rem, 4vw, 3rem)", fontWeight: 300 }}>
                    {s.closing1}<br />
                    {s.closing2} <em style={{ color: "#c4534a" }}>{s.closing3}</em>
                </p>
            </section><footer style={{ padding: "40px", textAlign: "center", fontSize: 10, letterSpacing: 2, color: "rgba(138,125,116,0.4)" }}>
            © {new Date().getFullYear()} NOW YOU CAN SEE LOVE — LAB
        </footer>
        </div>
    );
};
